<script setup lang="ts">
import { computed, ref } from "vue";

import ConfirmDialog from "@/components/base/ConfirmDialog.vue";
import VoiceCard from "@/components/gallery/VoiceCard.vue";
import VoicePreview from "@/components/lightbox/VoicePreview.vue";
import { pluralize } from "@/lib/format";
import { useAlbumStore } from "@/stores/album";
import { useGuestbookStore } from "@/stores/guestbook";
import { useNotificationsStore } from "@/stores/notifications";

/**
 * Livre d'or vocal, en section propre sous la mosaïque de photos.
 *
 * Les messages partagent le vocabulaire visuel de l'album — même bandeau de
 * titre, mêmes polaroïds en mosaïque — mais pas sa grille : les mélanger aux
 * photos ferait mentir le compte de souvenirs affiché en tête d'album.
 *
 * Ils portent en revanche le même moment de la journée, donc la barre de
 * filtres s'applique aussi à eux. L'onglet « Vidéos », lui, ne les concerne
 * pas : la section disparaît alors.
 */

const album = useAlbumStore();
const guestbook = useGuestbookStore();
const notifications = useNotificationsStore();

const visibleNotes = computed(() => {
  if (album.filter === "all") return guestbook.notes;
  if (album.filter === "video") return [];
  return guestbook.notes.filter((note) => note.moment === album.filter);
});

const openNoteId = ref<string | null>(null);
const pendingDeleteId = ref<string | null>(null);

const openNote = computed(
  () => guestbook.notes.find((note) => note.id === openNoteId.value) ?? null,
);

const countLabel = computed(() => pluralize(visibleNotes.value.length, "message", "messages"));

function onDeleteDialogOpenChange(open: boolean): void {
  if (!open) pendingDeleteId.value = null;
}

async function confirmDelete(): Promise<void> {
  const id = pendingDeleteId.value;
  pendingDeleteId.value = null;
  if (!id) return;
  openNoteId.value = null;
  await guestbook.removeNote(id);
  notifications.notify("Le message a été supprimé de cet appareil.", "success");
}
</script>

<template>
  <section
    v-if="visibleNotes.length"
    class="voice-section"
    data-testid="voice-section"
    aria-labelledby="voiceTitle"
  >
    <div class="voice-section__heading">
      <div>
        <span class="section-kicker">Livre d’or</span>
        <h2 id="voiceTitle">Leurs mots,<br /><em>dits à voix haute.</em></h2>
      </div>
      <div class="voice-section__stats">
        <strong data-testid="voice-count">{{ visibleNotes.length }}</strong>
        <span>{{ countLabel }}</span>
      </div>
    </div>

    <div class="gallery" data-testid="voice-gallery">
      <VoiceCard
        v-for="note in visibleNotes"
        :key="note.id"
        :note="note"
        @open="(id) => (openNoteId = id)"
      />
    </div>
  </section>

  <VoicePreview
    :note="openNote"
    @close="openNoteId = null"
    @delete="(id) => (pendingDeleteId = id)"
  />

  <ConfirmDialog
    :open="pendingDeleteId !== null"
    title="Supprimer ce message ?"
    message="Cet enregistrement sera définitivement retiré de cet appareil."
    confirm-label="Supprimer"
    tone="danger"
    @update:open="onDeleteDialogOpenChange"
    @confirm="() => void confirmDelete()"
  />
</template>

<style scoped>
.voice-section {
  max-width: 1120px;
  margin: 70px auto 0;
  padding-top: 50px;
  border-top: 1px solid rgb(183 114 101 / 0.2);
}

.voice-section__heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 20px;
}

.section-kicker {
  color: var(--color-rose-strong);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.17em;
  text-transform: uppercase;
}

.voice-section__heading h2 {
  margin: 9px 0 0;
  font-family: var(--font-serif);
  font-size: clamp(30px, 7vw, 54px);
  font-weight: 400;
  line-height: 0.92;
  letter-spacing: -0.035em;
  -webkit-text-stroke: 0.22px currentColor;
}

.voice-section__heading h2 em {
  color: var(--color-rose);
  font-family: var(--font-script);
  font-size: 0.95em;
  font-style: normal;
}

.voice-section__stats {
  flex: 0 0 auto;
  text-align: right;
}

.voice-section__stats strong,
.voice-section__stats span {
  display: block;
}

.voice-section__stats strong {
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 400;
  -webkit-text-stroke: 0.15px currentColor;
}

.voice-section__stats span {
  color: var(--color-muted);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* Mêmes colonnes et mêmes rotations que la mosaïque de photos : les deux
   sections doivent se lire comme un seul album. */
.gallery {
  columns: 2;
  column-gap: 13px;
  padding: 12px 3px;
}

.gallery .media-card {
  transform: rotate(-1.2deg);
  transform-origin: center;
}

.gallery .media-card:nth-child(2n) {
  transform: rotate(1.4deg);
}

.gallery .media-card:nth-child(3n) {
  transform: rotate(-2deg);
}

.gallery .media-card:hover,
.gallery .media-card:focus-within {
  transform: rotate(0) translateY(-5px) scale(1.015);
}

@media (min-width: 760px) {
  .gallery {
    columns: 4;
    column-gap: 24px;
    padding: 18px 12px;
  }
}
</style>
